// Seed data for the local workflow-engine stub (see `workflow-stub.ts` for the
// resolver/guard logic that reads it). Split into its own file purely to stay
// under the `max-lines` guardrail — this is config data, not logic. It mirrors
// the backend seed matrix (`scripts/src/workflows/workflow-seed-matrix.ts`).

import { RequiredWorkOrderField } from '@atlas/types'
import {
	HookCode,
	WorkflowPhase,
	type WorkflowStatusSnapshot,
	type WorkflowTransitionSnapshot,
	WorkspaceCode,
} from '@/domain/workflow.types'

export const DEFAULT_WORKFLOW_ID = 'default'

/** The three sequential validation lanes, in order. */
export const VALIDATION_LANE_ORDER = ['internal', 'design', 'compliance', 'source_material'] as const

export const VALIDATION_LANE_LABEL: Readonly<Record<(typeof VALIDATION_LANE_ORDER)[number], string>> = {
	internal: 'Internal validation',
	design: 'Design validation',
	compliance: 'Compliance validation',
	source_material: 'Source material',
}

/** The default workflow: intake (with the source-material check) → design → internal and external review → distribution package → completed, or cancelled. Status `id` doubles as `code` — this stub has no real row ids yet. */
export const DEFAULT_WORKFLOW_STATUSES: readonly WorkflowStatusSnapshot[] = [
	{
		id: 'created',
		code: 'created',
		label: 'Created',
		phase: WorkflowPhase.INTAKE,
		position: 0,
		initial: true,
		terminal: false,
		workspace: null,
	},
	{
		id: 'backlog',
		code: 'backlog',
		label: 'Backlog',
		phase: WorkflowPhase.INTAKE,
		position: 1,
		initial: false,
		terminal: false,
		workspace: null,
	},
	{
		id: 'review_source_material',
		code: 'review_source_material',
		label: 'Review source material',
		phase: WorkflowPhase.INTAKE,
		position: 2,
		initial: false,
		terminal: false,
		workspace: null,
	},
	{
		id: 'pending_source_material',
		code: 'pending_source_material',
		label: 'Pending source material',
		phase: WorkflowPhase.INTAKE,
		position: 3,
		initial: false,
		terminal: false,
		workspace: null,
	},
	{
		id: 'sources_verified',
		code: 'sources_verified',
		label: 'Sources verified',
		phase: WorkflowPhase.INTAKE,
		position: 4,
		initial: false,
		terminal: false,
		workspace: null,
	},
	{
		id: 'ready_to_design',
		code: 'ready_to_design',
		label: 'Ready to design',
		phase: WorkflowPhase.PRODUCTION,
		position: 5,
		initial: false,
		terminal: false,
		workspace: null,
	},
	{
		id: 'in_progress',
		code: 'in_progress',
		label: 'In progress',
		phase: WorkflowPhase.PRODUCTION,
		position: 6,
		initial: false,
		terminal: false,
		workspace: null,
	},
	{
		id: 'internal_review',
		code: 'internal_review',
		label: 'Internal review',
		phase: WorkflowPhase.VALIDATION,
		position: 7,
		initial: false,
		terminal: false,
		workspace: null,
	},
	{
		id: 'external_review',
		code: 'external_review',
		label: 'External review',
		phase: WorkflowPhase.VALIDATION,
		position: 8,
		initial: false,
		terminal: false,
		workspace: null,
	},
	{
		id: 'changes_requested',
		code: 'changes_requested',
		label: 'Changes requested',
		phase: WorkflowPhase.PRODUCTION,
		position: 9,
		initial: false,
		terminal: false,
		workspace: null,
	},
	{
		id: 'upload_distribution_package',
		code: 'upload_distribution_package',
		label: 'Upload distribution package',
		phase: WorkflowPhase.DELIVERY,
		position: 10,
		initial: false,
		terminal: false,
		workspace: null,
	},
	{
		id: 'completed',
		code: 'completed',
		label: 'Completed',
		phase: WorkflowPhase.DELIVERY,
		position: 11,
		initial: false,
		terminal: true,
		workspace: null,
	},
	{
		id: 'cancelled',
		code: 'cancelled',
		label: 'Cancelled',
		phase: WorkflowPhase.DELIVERY,
		position: 12,
		initial: false,
		terminal: true,
		workspace: null,
	},
] as const

/** The fields the seeded Confirm request requires. */
const CREATED_REQUIRED_FIELDS = [
	RequiredWorkOrderField.ASSET_TYPE,
	RequiredWorkOrderField.COMPLEXITY,
	RequiredWorkOrderField.BRAND,
	RequiredWorkOrderField.MARKET,
	RequiredWorkOrderField.DUE_DATE,
]

/** The fields the seeded Send Task requires. */
const SEND_TASK_REQUIRED_FIELDS = [
	RequiredWorkOrderField.ASSET_TYPE,
	RequiredWorkOrderField.PLATFORM,
	RequiredWorkOrderField.MAIN_COPY,
	RequiredWorkOrderField.CAMPAIGN_MATERIALS,
]

/** The intake moves and Send Task; the design steps and cancel are added below. */
const INTAKE_TRANSITIONS: Readonly<Record<string, readonly WorkflowTransitionSnapshot[]>> = {
	created: [
		{
			transitionId: 'stub-created-backlog',
			toStatusId: 'backlog',
			direction: 'forward',
			ctaLabel: 'Confirm request',
			guards: [{ hookCode: HookCode.FIELDS_PRESENT, params: { fields: CREATED_REQUIRED_FIELDS } }],
			hasChecklist: false,
		},
	],
	backlog: [
		{
			transitionId: 'stub-backlog-review_source_material',
			toStatusId: 'review_source_material',
			direction: 'forward',
			ctaLabel: 'Request materials validation',
			guards: [{ hookCode: HookCode.ASSIGNEE_PRESENT }],
			hasChecklist: false,
		},
		{
			transitionId: 'stub-backlog-sources_verified',
			toStatusId: 'sources_verified',
			direction: 'forward',
			ctaLabel: 'Submit',
			guards: [{ hookCode: HookCode.ASSIGNEE_PRESENT }],
			hasChecklist: false,
		},
	],
	review_source_material: [
		{
			transitionId: 'stub-review_source_material-sources_verified',
			toStatusId: 'sources_verified',
			direction: 'forward',
			ctaLabel: 'Submit',
			guards: [{ hookCode: HookCode.CHANGE_REQUEST_RESOLVED, params: { lane: 'source_material' } }],
			hasChecklist: false,
		},
		{
			transitionId: 'stub-review_source_material-pending_source_material',
			toStatusId: 'pending_source_material',
			direction: 'forward',
			ctaLabel: 'Submit',
			guards: [{ hookCode: HookCode.CHANGE_REQUEST_CREATED, params: { lane: 'source_material' } }],
			hasChecklist: false,
		},
	],
	pending_source_material: [
		{
			transitionId: 'stub-pending_source_material-review_source_material',
			toStatusId: 'review_source_material',
			direction: 'forward',
			ctaLabel: 'Request materials verification',
			guards: [],
			hasChecklist: false,
		},
		{
			transitionId: 'stub-pending_source_material-sources_verified',
			toStatusId: 'sources_verified',
			direction: 'forward',
			ctaLabel: 'Submit',
			guards: [],
			hasChecklist: false,
		},
	],
	sources_verified: [
		{
			transitionId: 'stub-sources_verified-ready_to_design',
			toStatusId: 'ready_to_design',
			direction: 'forward',
			ctaLabel: 'Send Task',
			guards: [{ hookCode: HookCode.FIELDS_PRESENT, params: { fields: SEND_TASK_REQUIRED_FIELDS } }],
			hasChecklist: false,
		},
	],
}

/** The design and review steps, like the backend seed: only the design hand-over is guarded. */
const STEP_MOVES: readonly (readonly [from: string, to: string, ctaLabel: string])[] = [
	['ready_to_design', 'in_progress', 'Start design'],
	['in_progress', 'internal_review', 'Submit for validation'],
	['internal_review', 'external_review', 'Send to external review'],
	['internal_review', 'changes_requested', 'Request changes'],
	['external_review', 'upload_distribution_package', 'Approve'],
	['external_review', 'changes_requested', 'Request changes'],
	['changes_requested', 'in_progress', 'Resume design'],
	['upload_distribution_package', 'completed', 'Complete'],
]

const move = (from: string, to: string, ctaLabel: string): WorkflowTransitionSnapshot => ({
	transitionId: `stub-${from}-${to}`,
	toStatusId: to,
	direction: 'forward',
	ctaLabel,
	guards: from === 'in_progress' && to === 'internal_review' ? [{ hookCode: HookCode.DESIGN_DRAFTS_SUBMITTED }] : [],
	hasChecklist: false,
})

/** Transitions legal from each status id: its configured moves, then Cancel from every open status. */
export const TRANSITIONS_BY_STATUS: Readonly<Record<string, readonly WorkflowTransitionSnapshot[]>> =
	Object.fromEntries(
		DEFAULT_WORKFLOW_STATUSES.map((status) => [
			status.id,
			status.terminal
				? []
				: [
						...(INTAKE_TRANSITIONS[status.id] ?? []),
						...STEP_MOVES.filter(([from]) => from === status.id).map(([from, to, label]) => move(from, to, label)),
						move(status.id, 'cancelled', 'Cancel order'),
					],
		]),
	)

/** Status id → resolved workspace kind, per the confirmed six-kind mapping. */
export const WORKSPACE_BY_STATUS: Readonly<Record<string, WorkspaceCode>> = {
	created: WorkspaceCode.QUEUE_CARD,
	backlog: WorkspaceCode.QUEUE_CARD,
	review_source_material: WorkspaceCode.QUEUE_CARD,
	pending_source_material: WorkspaceCode.QUEUE_CARD,
	sources_verified: WorkspaceCode.QUEUE_CARD,
	ready_to_design: WorkspaceCode.RESOLUTION,
	in_progress: WorkspaceCode.PRODUCTION,
	internal_review: WorkspaceCode.VALIDATION,
	external_review: WorkspaceCode.VALIDATION,
	changes_requested: WorkspaceCode.PRODUCTION,
	upload_distribution_package: WorkspaceCode.DISTRIBUTION,
	completed: WorkspaceCode.SUMMARY,
	cancelled: WorkspaceCode.SUMMARY,
}
